import { SkyButton, SkyDialog, SkyText } from "@skypointcloud/skypoint-ui";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import type { DbtJobRunRow, DbtJobView, TransformationGraph } from "@shared/types/transformations";
import {
  EmptyState,
  ErrorState,
  LoadingRows,
  PermissionNotice,
} from "@/components/access/ScreenStates";
import { TopbarActionsPortal } from "@/components/navigation/TopbarActionsPortal";
import { CountPill } from "@/components/transformations/CountPill";
import { RunDbtJobsDialog } from "@/components/transformations/RunDbtJobsDialog";
import {
  TRANSFORMATION_ACTION_BAR_ID,
  TransformationActionBars,
} from "@/components/transformations/TransformationActionBars";
import { TransformationDetailsDrawer } from "@/components/transformations/TransformationDetailsDrawer";
import { DbtJobDrawer } from "@/components/transformations/DbtJobDrawer";
import { TransformationHistoryView } from "@/components/transformations/TransformationHistoryView";
import {
  TransformationModelsView,
  useLandingParam,
  type GraphState,
} from "@/components/transformations/TransformationModelsView";
import {
  TRANSFORMATION_STATUS_LINE_ID,
  TransformationStatusLine,
} from "@/components/transformations/TransformationStatusLine";
import { TransformationTestsView } from "@/components/transformations/TransformationTestsView";
import { DbtJobsGrid } from "@/components/transformations/DbtJobsGrid";
import {
  isRunnable,
  runNowPageBlock,
  lastFinishedStep,
  transformStepPresentation,
} from "@/components/transformations/transformFormatting";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { useApiErrorNotice } from "@/lib/feedback/useApiErrorNotice";
import { usePermissions } from "@/hooks/use-permissions";
import { RUN_POLL_INTERVAL_MS, usePollWhile } from "@/hooks/use-runs";
import { useQueryClient } from "@tanstack/react-query";
import {
  refetchNewestRuns,
  DBT_JOBS_GRAPH_PATH,
  useDeleteDbtJob,
  useTransformationGraph,
  useDbtJobRuns,
  useDbtJobs,
  useUpdateDbtJob,
  type DbtJobsResult,
} from "@/hooks/use-dbt-jobs";
import { AddIcon, PlayIcon } from "@/lib/icons";
import { DBT_SETTINGS_PLACE } from "@/lib/transformations/dbtSettings";
import { landingFacts, projectCounts } from "@/lib/transformations/landingFacts";
import { runSelectedAs } from "@/lib/transformations/runSelected";
import {
  LANDING_VIEWS,
  landingBar,
  landingStatusLine,
  landingViewOf,
  type LandingBar,
  type LandingView,
  type StatusLine,
} from "@/lib/transformations/landingStatus";
import { RefreshErrorBar } from "@/components/states/RefreshErrorBar";

/**
 * The Transformations landing (canvas 15; FR-62, FR-13, UX-3, SEC-17; Session 11 P5,
 * ux-s11-transformations.md §2, binding).
 *
 * Reading order, top to bottom: the facts strip (Repository, Last run, Models "4 of 5 built",
 * Tests "23 of 33 passed"; owner feedback 2026-09-28), at most one action bar (only when something
 * needs action), the view switch (Models, the default · Tests · History · Transformations, each
 * count a pill after its label; `?view=`), and the view. The always-on success banner and the
 * "Last transform step" card are gone (owner item 4); their facts are in the Details drawer.
 *
 * Three reads, as before: the transformations, the graph (read once per visit and on Refresh,
 * never polled: its `bundle` is the binding as it is now, never `/instances/current/binding`,
 * which is audited) and the newest transform step, re-read every 5 s ONLY while it is in flight.
 * The strip, its announcement and the bar are derived from the last two by the pure
 * `landingFacts.ts` and `landingStatus.ts`; the switch's counts and the strip's share
 * `projectCounts()`, so "Models 5" and "4 of 5 built" are the same 5.
 *
 * The Transformations view renders under every binding state: transformations are prepared
 * before an instance admin or above binds the repository, and only running is blocked, with the
 * reason on the status line or the bar (the run button's `aria-describedby`), never a greyed
 * button alone. Viewer-safe: writes are absent, not disabled.
 */

type DrawerState = { mode: "closed" } | { mode: "create" } | { mode: "edit"; row: DbtJobView };

/** Round 6 (T3): what a transformation is, beside the models it selects. */
const TRANSFORMATION_EXPLAINER =
  "A dbt job runs the models one selector picks, such as tag:staging. dbt builds them in dependency order.";

/** Round 6 (W12): why New dbt job waits for a repository. */
const NEW_TRANSFORMATION_UNBOUND_REASON = `Set the dbt repository under ${DBT_SETTINGS_PLACE} first: a dbt job selects models from it.`;
const NEW_TRANSFORMATION_REASON_ID = "transformation-new-reason";

const VIEW_LABELS: Record<LandingView, string> = {
  models: "Models",
  tests: "Tests",
  history: "History",
  // Round 6 T-29: the saved dbt selections are dbt jobs; the page keeps the layer's name.
  transformations: "Jobs",
};

interface ViewCounts {
  models: number | null;
  tests: number | null;
  transformations: number | null;
}

/** A read's state for the landing: an answered read stays "ready" through a failed refetch. */
function queryStateOf(query: { data: unknown; isError: boolean }): GraphState {
  if (query.data) return "ready";
  return query.isError ? "error" : "loading";
}

function viewCount(value: LandingView, counts: ViewCounts): number | null {
  if (value === "models") return counts.models;
  if (value === "tests") return counts.tests;
  if (value === "transformations") return counts.transformations;
  return null;
}

/**
 * The bar, else the status line, carries the reason when nothing can run; a screen reader hears
 * it with the button rather than after the click. The line is a skeleton without its id until
 * the runs read answers: no target then.
 */
function runNowDescribedBy(
  ready: boolean,
  bar: LandingBar | null,
  line: StatusLine,
): string | undefined {
  if (ready) return undefined;
  if (bar) return TRANSFORMATION_ACTION_BAR_ID;
  return line.state === "loading" ? undefined : TRANSFORMATION_STATUS_LINE_ID;
}

/**
 * UX §3.5: closing the panel hands focus back to "Details". After the commit that closed it,
 * and on the next task, so the library's own focus restore (to the body when the opener was
 * not focused, as after a pointer click in some browsers) cannot win over it.
 */
function useDetailsFocusReturn(detailsOpen: boolean) {
  const detailsButton = useRef<HTMLButtonElement>(null);
  const detailsWasOpen = useRef(false);
  useEffect(() => {
    if (detailsOpen) {
      detailsWasOpen.current = true;
      return;
    }
    if (!detailsWasOpen.current) return;
    detailsWasOpen.current = false;
    const timer = setTimeout(() => detailsButton.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, [detailsOpen]);
  return detailsButton;
}

function viewLabel(value: LandingView, counts: ViewCounts): ReactNode {
  const count = viewCount(value, counts);
  return (
    <>
      {VIEW_LABELS[value]}
      {/* No failed badge here: the strip's Tests fact says the failures once (review r1, S1). */}
      {count !== null ? (
        <CountPill count={count} testId={`transformation-view-${value}-count`} />
      ) : null}
    </>
  );
}

interface TopbarArgs {
  canStart: boolean;
  canWrite: boolean;
  unbound: boolean;
  describedBy: string | undefined;
  runNowDisabled: boolean;
  newDisabled: boolean;
  onRunNow: () => void;
  onCreate: () => void;
}

function topbarActions(args: TopbarArgs): ReactNode {
  const { canStart, canWrite, unbound } = args;
  return (
    <TopbarActionsPortal>
      {canStart ? (
        <SkyButton
          appearance="secondary"
          icon={PlayIcon}
          onClick={args.onRunNow}
          aria-describedby={args.describedBy}
          // Not live until the binding (the graph's `bundle`) has answered, so
          // the dialog never opens claiming "not bound" about a binding that
          // simply has not loaded yet — or that could not be read at all: the
          // Retry in the view and in Details is the way forward then, not the dialog.
          // Walk 2026-10-02, D8: and with no repository, as New dbt job is; the bar
          // (the button's description) says why, and the server refuses the run too (422).
          disabled={args.runNowDisabled}
          testId="run-transformations-now"
        >
          Run all dbt jobs now
        </SkyButton>
      ) : null}
      {canWrite ? (
        <>
          <SkyButton
            appearance="primary"
            icon={AddIcon}
            onClick={args.onCreate}
            disabled={args.newDisabled}
            aria-describedby={unbound ? NEW_TRANSFORMATION_REASON_ID : undefined}
            testId="transformation-new"
          >
            New dbt job
          </SkyButton>
          {unbound ? (
            <span id={NEW_TRANSFORMATION_REASON_ID} className="sr-only">
              {NEW_TRANSFORMATION_UNBOUND_REASON}
            </span>
          ) : null}
        </>
      ) : null}
    </TopbarActionsPortal>
  );
}

/** #841: the newest transform steps read, enough to find the newest finished one past a queue. */
const LAST_STEPS_PAGE = 5;

interface JobsViewArgs {
  transformations: UseQueryResult<DbtJobsResult>;
  rows: DbtJobView[];
  graph: TransformationGraph | undefined;
  lastStep: DbtJobRunRow | null;
  /** #841: the newer step in flight while `lastStep` is the newest finished one. */
  inFlight: DbtJobRunRow | null;
  canWrite: boolean;
  /** `runs.start`: Run now in each job's menu (#1293). */
  canStart: boolean;
  unbound: boolean;
  onCreate: () => void;
  /** #1293: a run of this one job only. */
  onRunJob: (row: DbtJobView) => void;
  onEdit: (row: DbtJobView) => void;
  onSetEnabled: (row: DbtJobView, enabled: boolean) => void;
  onDelete: (row: DbtJobView) => void;
}

function emptyJobsState(args: JobsViewArgs): ReactNode {
  const { canWrite, unbound } = args;
  return (
    <EmptyState
      title="No dbt jobs yet"
      message={
        unbound && canWrite
          ? `A dbt job runs the models one selector picks, such as tag:staging, in the destination after data lands. ${NEW_TRANSFORMATION_UNBOUND_REASON}`
          : "A dbt job runs the models one selector picks, such as tag:staging, in the destination after data lands. Create one to pick which models run. A dbt job without a selector is left out of the run, never widened to the whole project."
      }
      action={
        canWrite ? (
          <SkyButton
            appearance="primary"
            icon={AddIcon}
            onClick={args.onCreate}
            disabled={unbound}
            testId="transformation-new-empty"
          >
            New dbt job
          </SkyButton>
        ) : undefined
      }
    />
  );
}

function jobsViewContent(args: ViewBodyArgs): ReactNode {
  const { transformations, rows } = args;
  if (transformations.isPending) return <LoadingRows rows={4} columns={7} />;
  // #1398: only a failed first read replaces the list; a failed re-read keeps the rows.
  if (transformations.isError && transformations.data === undefined) {
    return (
      <ErrorState
        title="We could not load the dbt jobs"
        error={transformations.error}
        onRetry={() => void transformations.refetch()}
      />
    );
  }
  if (!transformations.isError) return jobsViewBody(args);
  return (
    <>
      <RefreshErrorBar
        things="dbt jobs"
        error={transformations.error}
        onRetry={() => void transformations.refetch()}
        empty={rows.length === 0}
        testId="transformations-refresh-error"
      />
      {jobsViewBody(args)}
    </>
  );
}

/** The dbt jobs read, as the list shows it: not available here, none yet, or the grid. */
function jobsViewBody(args: ViewBodyArgs): ReactNode {
  const { transformations, rows } = args;
  if (transformations.data === null) {
    return (
      <EmptyState
        title="Transformations are not available in this deployment"
        message="The dbt jobs API is not mounted here. Nothing is lost: connections and schedules still run."
      />
    );
  }
  if (rows.length === 0) return emptyJobsState(args);
  return (
    <>
      {/* Round 6 (T3, owner): Models and Transformations, said once where both meet. */}
      <SkyText size={200} color="muted" block className="mb-2" testId="transformations-explainer">
        {TRANSFORMATION_EXPLAINER}
      </SkyText>
      <DbtJobsGrid
        rows={rows}
        graph={args.graph}
        lastStep={args.lastStep}
        inFlight={args.inFlight}
        canWrite={args.canWrite}
        canStart={args.canStart}
        onRunNow={args.onRunJob}
        runNowBlock={runNowPageBlock(args.graphState, args.unbound)}
        onEdit={args.onEdit}
        onSetEnabled={args.onSetEnabled}
        onDelete={args.onDelete}
      />
    </>
  );
}

interface ViewBodyArgs extends JobsViewArgs {
  view: LandingView;
  graphState: GraphState;
  canStart: boolean;
  onRetryGraph: () => void;
  onRunNow: () => void;
  onRunSelected: ((models: string[], every: boolean) => void) | undefined;
}

function landingViewBody(args: ViewBodyArgs): ReactNode {
  const { view, canStart, canWrite } = args;
  if (view === "models") {
    return (
      <TransformationModelsView
        graph={args.graph}
        graphState={args.graphState}
        onRetry={args.onRetryGraph}
        canStart={canStart}
        onRunNow={args.onRunNow}
        onRefresh={args.onRetryGraph}
        onNewDbtJob={canWrite ? args.onCreate : undefined}
        onRunSelected={args.onRunSelected}
      />
    );
  }
  if (view === "tests") {
    return (
      <TransformationTestsView
        graph={args.graph}
        graphState={args.graphState}
        onRetry={args.onRetryGraph}
        canStart={canStart}
        transformations={args.rows}
        onRefresh={args.onRetryGraph}
        onNewDbtJob={canWrite ? args.onCreate : undefined}
      />
    );
  }
  if (view === "history") return <TransformationHistoryView canStart={canStart} />;
  return <div data-testid="transformation-transformations-view">{jobsViewContent(args)}</div>;
}

interface RunDialogsArgs {
  canStart: boolean;
  runNowOpen: boolean;
  setRunNowOpen: (open: boolean) => void;
  /** #1293: the dbt job whose Run now is open. */
  runJob: DbtJobView | null;
  setRunJob: (row: DbtJobView | null) => void;
  everyModelRun: boolean;
  setEveryModelRun: (open: boolean) => void;
  chosenToRun: string[] | null;
  setChosenToRun: (models: string[] | null) => void;
  rows: DbtJobView[];
  bundle: TransformationGraph["bundle"] | undefined;
  graph: TransformationGraph | undefined;
  runInFlight: boolean;
}

function runDialogs(args: RunDialogsArgs): ReactNode {
  const { canStart, rows, bundle, runInFlight, chosenToRun, setChosenToRun } = args;
  return (
    <>
      {canStart && args.runNowOpen ? (
        <RunDbtJobsDialog
          onOpenChange={args.setRunNowOpen}
          transformations={rows}
          bundle={bundle}
          runInFlight={runInFlight}
        />
      ) : null}

      {canStart && args.runJob ? (
        <RunDbtJobsDialog
          onOpenChange={(open) => {
            if (!open) args.setRunJob(null);
          }}
          transformations={rows}
          bundle={bundle}
          runInFlight={runInFlight}
          onlyJobId={args.runJob.id}
        />
      ) : null}

      {canStart && args.everyModelRun ? (
        <RunDbtJobsDialog
          onOpenChange={args.setEveryModelRun}
          transformations={rows}
          bundle={bundle}
          runInFlight={runInFlight}
          everyModelChosen
        />
      ) : null}

      {canStart && chosenToRun ? (
        <RunDbtJobsDialog
          onOpenChange={(open) => {
            if (!open) setChosenToRun(null);
          }}
          transformations={rows}
          bundle={bundle}
          runInFlight={runInFlight}
          chosenModels={chosenToRun}
          graph={args.graph}
        />
      ) : null}
    </>
  );
}

function deleteDialog(
  pendingDelete: DbtJobView | null,
  setPendingDelete: (row: DbtJobView | null) => void,
  doDelete: () => Promise<void>,
  deleting: boolean,
): ReactNode {
  if (!pendingDelete) return null;
  return (
    <SkyDialog
      open
      onOpenChange={(open) => {
        if (!open) setPendingDelete(null);
      }}
      title={`Delete ${pendingDelete.name}?`}
      description="Schedules that name it drop it. Runs already recorded keep their history."
      size="small"
      testId="transformation-delete-dialog"
      primaryAction={{
        label: "Delete",
        destructive: true,
        // Returned, not voided: `SkyDialog` awaits the primary action
        // before it closes itself, so the dialog stays until the DELETE
        // has landed.
        onClick: doDelete,
        loading: deleting,
      }}
      secondaryAction={{ label: "Keep it", onClick: () => setPendingDelete(null) }}
    />
  );
}

export default function TransformationsListPage() {
  const { canAny, isResolved } = usePermissions();
  const notice = useNotice();
  const showApiError = useApiErrorNotice();

  const canRead = canAny("dbt_jobs.read");
  const canWrite = canAny("dbt_jobs.write");
  const canStart = canAny("runs.start");

  const transformations = useDbtJobs(canRead);
  const graph = useTransformationGraph(canRead);
  // #841: a small page, so a step in flight can be read past to the newest finished one.
  const lastRuns = useDbtJobRuns(LAST_STEPS_PAGE, canRead);
  const queryClient = useQueryClient();
  const update = useUpdateDbtJob();
  const remove = useDeleteDbtJob();

  const [drawer, setDrawer] = useState<DrawerState>({ mode: "closed" });
  const [runNowOpen, setRunNowOpen] = useState(false);
  const [runJob, setRunJob] = useState<DbtJobView | null>(null);
  // Round 6 (T3): the models "Run selected" chose; a separate open of the same dialog.
  const [chosenToRun, setChosenToRun] = useState<string[] | null>(null);
  const [everyModelRun, setEveryModelRun] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<DbtJobView | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const detailsButton = useDetailsFocusReturn(detailsOpen);
  const [viewParam, setViewParam] = useLandingParam("view");
  const view = landingViewOf(viewParam);

  const lastStep = lastRuns.data?.rows[0] ?? null;
  const lastFinished = lastFinishedStep(lastRuns.data?.rows ?? []);
  const runInFlight = lastStep ? transformStepPresentation(lastStep).isActive : false;

  // The status line, the bar and the dialog's in-flight warning follow a step in flight, as the
  // detail header does; without this, "Queued" stays until a reload. The graph is not polled; it is read once when the step ends (below).
  // #841: every newest-runs read, so the Models and Tests views' one-row reads follow it too.
  usePollWhile(runInFlight, RUN_POLL_INTERVAL_MS, () => {
    void refetchNewestRuns(queryClient);
  });

  // #1260: the graph describes the last launched run, so it is read again the moment a step in
  // flight ends. Without this the Tests card keeps "Not run yet" and every job row reads the whole
  // run ("Whole run"), because the graph's run is no longer the newest step, until a reload.
  const wasInFlight = useRef(false);
  useEffect(() => {
    if (wasInFlight.current && !runInFlight) {
      void queryClient.invalidateQueries({ queryKey: [DBT_JOBS_GRAPH_PATH] });
    }
    wasInFlight.current = runInFlight;
  }, [runInFlight, queryClient]);

  // A graph that answered once stays "ready" through a failed refetch (its facts are still true).
  const graphState: GraphState = queryStateOf(graph);
  const runsState = queryStateOf(lastRuns);
  const landing = {
    bundle: graph.data?.bundle,
    graphState,
    newest: lastStep,
    runsState,
  } as const;
  const line = landingStatusLine(landing);
  const bar = landingBar(landing);
  // Counts on the view switch, when known (UX §2.4), as pills after the labels. The strip is
  // handed the same value, so its "4 of 5 built" and the switch's "Models 5" are one number.
  const project = useMemo(() => projectCounts(graph.data), [graph.data]);
  const facts = landingFacts(
    { graph: graph.data, graphState, newest: lastStep, runsState },
    project,
  );
  const counts: ViewCounts = {
    models: project?.models ?? null,
    tests: project?.tests ?? null,
    transformations: Array.isArray(transformations.data) ? transformations.data.length : null,
  };

  // See `pages/admin/users.tsx`: a denial rendered before `/api/v2/me` answers is
  // a denial shown to somebody who is allowed.
  if (!isResolved) return <LoadingRows rows={4} columns={7} />;
  if (!canRead) return <PermissionNotice permission="dbt_jobs.read" />;

  const rows = transformations.data ?? [];
  const bundle = graph.data?.bundle;
  // Runnable is the runner's rule: enabled AND carrying a selector.
  const ready = Boolean(bundle?.repoBound) && rows.some(isRunnable);
  // Round 6 (W12): a transformation selects models from the repository, so none is created before
  // one is bound (an existing one can still be edited). Unknown while the graph has not answered.
  const unbound = bundle !== undefined && !bundle.repoBound;

  const selectView = (next: LandingView): void => {
    // `?result=` belongs to the view it filtered; a new view starts unfiltered.
    setViewParam(next === "models" ? null : next, { result: null });
  };
  // "Show models" / "Show tests" (UX §2.5): `?view=models&result=failed` / `?view=tests&…`.
  const showFailed = (next: "models" | "tests"): void => {
    setViewParam(next, { result: "failed" });
  };

  const setEnabled = async (row: DbtJobView, enabled: boolean): Promise<void> => {
    try {
      await update.mutateAsync({ id: row.id, body: { enabled } });
      notice.success(row.name, {
        title: enabled ? "dbt job enabled" : "dbt job disabled",
      });
    } catch (error) {
      showApiError(error, "The dbt job could not be changed.");
    }
  };

  // Confirmed first (as the drawer and the connections list do): the menu item
  // sits one row below "Disable", and a misclick here is irreversible.
  const doDelete = async (): Promise<void> => {
    const row = pendingDelete;
    if (!row) return;
    try {
      await remove.mutateAsync(row.id);
      notice.success(row.name, { title: "dbt job deleted" });
      setPendingDelete(null);
    } catch (error) {
      setPendingDelete(null);
      showApiError(error, "The dbt job could not be deleted.");
    }
  };

  const openCreate = () => setDrawer({ mode: "create" });
  const openRunNow = () => setRunNowOpen(true);
  const retryGraph = () => void graph.refetch();
  // Round 6 T-29 (review r1): a run of the chosen models, every model included;
  // only every model past what one run takes is Run all dbt jobs now.
  const runSelected = (models: string[], every: boolean) => {
    if (runSelectedAs(models, every) === "all-jobs") setEveryModelRun(true);
    else setChosenToRun(models);
  };

  return (
    <div className="space-y-4">
      {topbarActions({
        canStart,
        canWrite,
        unbound,
        describedBy: runNowDescribedBy(ready, bar, line),
        runNowDisabled: transformations.isPending || graph.isPending || graph.isError || unbound,
        newDisabled: transformations.isPending || unbound,
        onRunNow: openRunNow,
        onCreate: openCreate,
      })}

      <TransformationStatusLine
        ref={detailsButton}
        line={line}
        facts={facts}
        runId={lastStep?.runId ?? null}
        onDetails={() => setDetailsOpen(true)}
      />

      <TransformationActionBars
        bar={bar}
        canOpenDestination={canAny("instance.binding.read")}
        onShow={showFailed}
      />

      {/* A pressed-button group rather than `SkyTabList` (the run page's reason: the library's
          tab triggers point `aria-controls` at a panel only it may render, which axe reports).
          At 375 px the row scrolls inside itself, never the page. */}
      <fieldset
        aria-label="Views"
        className="min-w-0 flex flex-nowrap items-center gap-2 overflow-x-auto border-b border-border pb-2"
        data-testid="transformation-views"
      >
        {LANDING_VIEWS.map((value) => (
          <SkyButton
            key={value}
            className="shrink-0"
            appearance={view === value ? "secondary" : "subtle"}
            size="small"
            aria-pressed={view === value}
            onClick={() => selectView(value)}
            testId={`transformation-view-${value}`}
          >
            {viewLabel(value, counts)}
          </SkyButton>
        ))}
      </fieldset>

      {landingViewBody({
        view,
        graph: graph.data,
        graphState,
        canStart,
        canWrite,
        unbound,
        transformations,
        rows,
        onRunJob: setRunJob,
        lastStep: lastFinished.shown,
        inFlight: lastFinished.inFlight,
        onRetryGraph: retryGraph,
        onRunNow: openRunNow,
        onCreate: openCreate,
        onRunSelected: canStart ? runSelected : undefined,
        onEdit: (row) => setDrawer({ mode: "edit", row }),
        onSetEnabled: (row, enabled) => void setEnabled(row, enabled),
        onDelete: (row) => setPendingDelete(row),
      })}

      {/* Closing hands focus back to "Details" (the effect above). */}
      <TransformationDetailsDrawer
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        graph={graph.data}
        graphState={graphState}
        graphError={graph.error}
        onRetry={retryGraph}
        newest={lastStep}
        runsState={runsState}
        runsError={lastRuns.error}
        onRetryRuns={() => void lastRuns.refetch()}
        canOpenDestination={canAny("instance.binding.read")}
        canEditDbtSettings={canWrite}
      />

      {deleteDialog(pendingDelete, setPendingDelete, doDelete, remove.isPending)}

      {/* Mounted only while open, so the fields start from the row every time. */}
      {canWrite && drawer.mode !== "closed" ? (
        <DbtJobDrawer
          transformation={drawer.mode === "edit" ? drawer.row : null}
          others={rows}
          onClose={() => setDrawer({ mode: "closed" })}
        />
      ) : null}

      {runDialogs({
        canStart,
        runNowOpen,
        setRunNowOpen,
        runJob,
        setRunJob,
        everyModelRun,
        setEveryModelRun,
        chosenToRun,
        setChosenToRun,
        rows,
        bundle,
        graph: graph.data,
        runInFlight,
      })}
    </div>
  );
}
